import { format, isValid } from "date-fns";
import { differenceInYears } from "date-fns/fp";

/**
 * 検索の開始日および終了日を取得する
 * @param nowDate - 取得対象となる日付の基準日 デフォルトでは現在時刻が取得される
 * @returns - 検索開始日と終了日
 */
export const getStartAndEndDate = (
  nowDate: Date = new Date()
): [Date, Date] => {
  const year = nowDate.getFullYear();
  const startDate = new Date(year, nowDate.getMonth() - 3, 1);
  const endDate = new Date(year, nowDate.getMonth() - 1, 0);
  return [startDate, endDate];
};

/**
 * 検索可能な日付の一番前の日付を取得する
 * @returns - 検索可能な日付の一番前の日付
 */
export const getSearchMinDate = (): Date => {
  return new Date(2012, 0, 1);
};

/**
 * 検索可能な日付の一番後ろの日付を取得する
 * @returns - 検索可能な日付の一番後ろの日付 今月の末日
 */
export const getSearchMaxDate = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0);
};

/**
 * 二つの日付が1年以上離れているかどうかを判定する
 * @param startDate - 開始日
 * @param endDate - 終了日
 * @returns - 二つの日付が1年以上離れていればTrue、それ以外はFalse
 * @description
 * differenceInYears(new Date('2023/12/31'), new Date('2023/1/1')); // 非うるう => 0
 * differenceInYears(new Date('2024/1/1'),   new Date('2023/1/1')); // 非うるう => 1
 * differenceInYears(new Date('2024/12/31'), new Date('2024/1/1')); // うるう   => 0
 * differenceInYears(new Date('2025/1/1'),   new Date('2024/1/1')); // うるう   => 1
 */
export const isDateDiffOverOneYear = (
  startDate: Date,
  endDate: Date
): boolean => {
  // うるう年対策のため、date-fnsを使用して一年を判定する
  return Math.abs(differenceInYears(startDate, endDate)) >= 1;
};

/**
 * Date型をyyyy-MM-ddの文字列に変換する関数
 * @param {Date | null} date
 * @returns {string | null}
 */
export const dateToString = (date: Date | null): string | null => {
  if (date === null || !isValid(date)) return null;

  return format(date, "yyyy-MM-dd");
};

/**
 * Dateオブジェクトから新たな年最終日のDateを作成する
 * @param {Date | null} date
 * @returns {Date | null}
 */
export const dateToYearEnd = (date: Date | null): Date | null => {
  if (date === null || !isValid(date)) return null;
  const d = new Date(date.getTime());
  // NOTE: Dateオブジェクトでは、月のインデックスは0から始まる(0～11)ため、12月は「11」で表現する
  d.setMonth(11);
  d.setDate(31);
  return d;
};

/**
 * Dateオブジェクトから新たな月最終日のDateを作成する
 * @param {Date | null} date
 * @returns {Date | null}
 */
export const dateToMonthEnd = (date: Date | null): Date | null => {
  if (date === null || !isValid(date)) return null;

  const year = date.getFullYear();
  const month = date.getMonth();

  // 選択された年月の月末日を取得。次の月の0日 = 今月の末日
  return new Date(year, month + 1, 0);
};
